// src/config/structuredDataConfig.ts
import { services, ServiceCard } from '@/data/services';

/**
 * Base organization information for structured data
 */
const organizationData = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Hoptira',
  legalName: 'Hoptira Labs LLC',
  url: 'https://hoptira.com',
  logo: 'https://hoptira.com/hoptira-logo-full-dark.svg',
  foundingDate: '2025',
  founders: [
    {
      '@type': 'Person',
      name: 'Praba Ponnambalam',
    },
  ],
  address: {
    '@type': 'PostalAddress',
    streetAddress: '30 N Gould St Ste R',
    addressLocality: 'Sheridan',
    addressRegion: 'WY',
    postalCode: '82801',
    addressCountry: 'US',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer support',
    email: 'hello@hoptira.com',
    telephone: '+1 (307) 655-6507',
  },
  telephone: '+1 (307) 655-6507',
  sameAs: [
    'https://facebook.com/hoptira',
    'https://instagram.com/hoptiralabs',
    'https://twitter.com/hoptira',
    'https://github.com/hoptira',
  ],
};

/**
 * Local business information for structured data
 */
const localBusinessData = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: 'Hoptira',
  image: 'https://hoptira.com/hoptira-logo-full-dark.svg',
  '@id': 'https://hoptira.com',
  url: 'https://hoptira.com',
  telephone: '+1 (307) 655-6507',
  priceRange: '$$',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '30 N Gould St Ste R',
    addressLocality: 'Sheridan',
    addressRegion: 'WY',
    postalCode: '82801',
    addressCountry: 'US',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: '44.7979',
    longitude: '-106.9559',
  },
  openingHoursSpecification: {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    opens: '09:00',
    closes: '17:00',
  },
};

/**
 * Create website structured data
 */
const websiteData = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Hoptira',
  url: 'https://hoptira.com',
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: 'https://hoptira.com/search?q={search_term_string}',
    },
    'query-input': 'required name=search_term_string',
  },
};

/**
 * Get structured data for homepage
 */
const getHomePageStructuredData = () => {
  return [organizationData, websiteData];
};

/**
 * Get structured data for about page
 */
const getAboutPageStructuredData = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    mainEntity: {
      ...organizationData,
    },
  };
};

/**
 * Get structured data for contact page
 */
const getContactPageStructuredData = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    mainEntity: {
      ...organizationData,
    },
  };
};

/**
 * Get structured data for service pages
 */
const getServicePageStructuredData = (serviceId?: string) => {
  // Base services page
  if (!serviceId) {
    return {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Our Services',
      description:
        'Explore our web, app, analytics, AI, and automation services',
      url: 'https://hoptira.com/services',
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: services.map(
          (service: ServiceCard, index: number) => ({
            '@type': 'ListItem',
            position: index + 1,
            item: {
              '@type': 'Service',
              name: service.title,
              url: `https://hoptira.com/services?tab=${service.id}`,
            },
          }),
        ),
      },
    };
  }

  // Specific service page
  const service = services.find((s: ServiceCard) => s.id === serviceId);
  if (!service) return getServicePageStructuredData();

  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: service.title,
    provider: organizationData,
    name: `${service.title} Services`,
    description: service.description,
    url: `https://hoptira.com/services?tab=${service.id}`,
  };
};

/**
 * Get structured data for FAQ page/section
 */
const getFaqStructuredData = (faqs: { question: string; answer: string }[]) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
};

export {
  organizationData,
  localBusinessData,
  websiteData,
  getHomePageStructuredData,
  getAboutPageStructuredData,
  getContactPageStructuredData,
  getServicePageStructuredData,
  getFaqStructuredData,
};
